/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { SetupExcpParam, ItemList } from 'models/setup/excp/excp';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import CustomGrid from 'components/grids/CustomGrid';
import { useMessageBar } from 'hooks/useMessageBar';
import { saveItemList } from 'apis/setup/excp/Item';
import { CommonUtil } from 'utils/CommonUtil';
import { downloadExcelTemplatesPost, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import { FileTypeName } from 'models/common/Common';
import useSessionStore from 'stores/useSessionStore';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { useCommonModal } from 'hooks/useCommonModal';
import { CrudCode } from 'models/common/Edit';
import { ControlFilldBG } from 'ui/theme/Color';
import _ from 'lodash';
import WJCellTextarea from 'components/inputs/WJCellTextarea';
import GridSettingButtons from 'components/grids/GridSettingButtons';

interface ListProps {
  id: string;
  rowData: ItemList[];
  itemCnt?: SetupExcpParam;
  excpParam: SetupExcpParam;
  onSearch: () => void;
}

const SetupItemList = ({ id, rowData, itemCnt, excpParam, onSearch }: ListProps) => {
  const { t } = useTranslation();
  const { userId } = useSessionStore();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();

  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);
  const [itemData, setItemData] = useState<ItemList[]>([]); //그리드
  const [isOpenCellTextarea, setOpenCellTextarea] = useState<boolean>(false);
  const [hitTest, setHitTest] = useState<any>();

  useEffect(() => {
    setItemData(rowData);
  }, [rowData]);

  useEffect(() => {
    if (!excpParam?.elmPrjId) return;
    onSearch();
  }, [id]);

  const layoutDefinition = [
    {
      binding: 'crudKey',
      header: String(t('com.label.상태', '상태')),
      minWidth: 43,
      width: 43,
      isReadOnly: true,
      align: 'center',
      cellTemplate: GridStatusCellTemplate,
      visible: isEmailFormat(),
    },
    {
      binding: 'bmkYn',
      header: String(t('mp.grid.발행대상', '발행대상')),
      width: 90,
      align: 'center',
      cssClass: 'WijmoBookmark',
      isReadOnly: true,
      cellTemplate: (ctx) =>
        `<button class="${ctx.value === 'Y' ? 'detail' : ''}" id="bmkBtn"></button>`,
      visible: id === 'LIST',
    },
    {
      binding: 'eqpId',
      header: String(t('com.label.M-CODE', 'M-CODE')),
      width: 150,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'tgtEqpNm',
      header: String(t('com.label.설비명', '설비명')),
      width: 300,
      isReadOnly: true,
      align: 'left',
    },
    {
      binding: 'excpNm',
      header: String(t('com.label.협력업체명', '협력업체명')),
      width: 140,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'pakItemNo',
      header: String(t('com.label.품목 No', '품목 No')),
      width: 120,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'pakItemNm',
      header: String(t('com.label.품목명', '품목명')),
      width: 300,
      isReadOnly: id === 'LIST',
      align: 'left',
    },
    {
      binding: 'pakItemQty',
      header: String(t('com.label.수량', '수량')),
      width: 100,
      isReadOnly: id === 'LIST',
      align: 'right',
      dataType: 'Number',
    },
    {
      binding: 'qtyUnitCd',
      header: String(t('com.label.단위', '단위')),
      width: 80,
      isReadOnly: id === 'LIST',
      align: 'center',
    },
    {
      header: String(t('com.label.진행현황', '진행현황')),
      columns: [
        {
          binding: 'arrivalYn',
          header: String(t('com.label.도착처리', '도착처리')),
          width: 120,
          isReadOnly: true,
          align: 'center',
          visible: id === 'LIST',
          cellTemplate: ({ value }) => {
            return value === 'Y' ? '완료' : '';
          },
        },
        {
          binding: 'planStDt',
          header: String(t('com.label.Fab-In 계획 일정', 'Fab-In 계획 일정')),
          width: 160,
          isReadOnly: true,
          align: 'center',
          visible: id === 'LIST',
        },
        {
          binding: 'fabinYn',
          header: String(t('com.label.Fab-In', 'Fab-In')),
          width: 120,
          isReadOnly: true,
          align: 'center',
          visible: id === 'LIST',
          cellTemplate: ({ value }) => {
            return value === 'Y' ? '완료' : '';
          },
        },
        {
          binding: 'abnormalYn',
          header: String(t('com.label.이상처리', '이상처리')),
          width: 120,
          isReadOnly: true,
          align: 'center',
          visible: id === 'LIST',
        },
      ],
    },
    {
      binding: 'boxNo',
      header: String(t('com.label.Plan Box No.', 'Plan Box No.')),
      width: 120,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'pakBoxNo',
      header: String(t('com.label.Box No.', 'Box No.')),
      width: 120,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'planStDt',
      header: String(t('com.label.Fab-In 계획 일정', 'Fab-In 계획 일정')),
      width: 160,
      isReadOnly: true,
      align: 'center',
      visible: id !== 'LIST',
    },
    {
      binding: 'lblIssuYn',
      header: String(t('setup.label.바코드 관리', '바코드 관리')),
      width: 110,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'rfidTagUseYn',
      header: String(t('setup.label.바코드 출력', '바코드 출력')),
      width: 100,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'dataUpdUserNm',
      header: String(t('com.label.수정자', '수정자')),
      width: 100,
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'dataUpdDtm',
      header: String(t('com.label.수정일자', '수정일자')),
      width: 100,
      isReadOnly: true,
      align: 'center',
    },
  ];

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      if (e.panel.cellType !== 1) return;

      const cell = e.cell;

      //스타일 초기화
      cell.style.color = '';
      cell.style.fontWeight = '';

      const col = s.columns[e.col];
      if (col.binding !== 'boxNo' && col.binding !== 'pakBoxNo') return;

      const item = s.rows[e.row]?.dataItem;
      if (!item) return;

      const boxNo = item.boxNo;
      const pakBoxNo = item.pakBoxNo;

      //두 값이 다를 때만 스타일 적용
      if (boxNo != null && pakBoxNo != null && boxNo !== pakBoxNo) {
        cell.style.color = '#F94B50';
        cell.style.fontWeight = '600';
      }
    });

    grid.beginningEdit.addHandler((s, e) => {
      const col = s.columns[e.col];

      if (col.binding === 'pakItemNm') {
        e.cancel = true;
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (!isEmailFormat()) return;
      const len = grid.rows.length;
      if (len == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return;
      const item = grid.rows[ht.row].dataItem;
      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.id) {
          case 'bmkBtn':
            item.bmkYn = item.bmkYn === null || item.bmkYn === 'N' ? 'Y' : null;
            item.crudKey = item.crudKey === null ? 'U' : null;
            gridRef.current.refresh();
            break;
        }
      }
    });

    grid.hostElement.addEventListener('dblclick', (e) => {
      const ht = grid.hitTest(e);
      setHitTest(ht);
      switch (ht.panel.columns[ht.col].binding) {
        case 'pakItemNm': {
          setOpenCellTextarea(true);
          break;
        }
      }
    });
  };

  const validateSelectedField = (grid: any, fieldKey: string, tailMsg: string): string | null => {
    const selectedRows = grid.selectedRows || [];

    if (!selectedRows.length) {
      return t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.');
    }

    const msgs: string[] = [];

    selectedRows.forEach((row: any) => {
      const data = row.dataItem ?? row._data ?? row;
      const value = data[fieldKey];

      const hasValue = value !== null && value !== undefined && value.toString().trim() !== '';

      if (hasValue) {
        const line = row.index !== undefined ? row.index + 1 : '?';
        msgs.push(`${line}행`);
      }
    });

    if (!msgs.length) return null;

    return `${msgs.join('\n')}\n${tailMsg}`;
  };

  const validateRrfidTagUseYn = (grid: any): string | null =>
    validateSelectedField(
      grid,
      'rfidTagUseYn',
      t(
        'com.label.- 해당 품목은 바코드가 발행되었습니다. 삭제 할 수 없습니다.',
        '- 해당 품목은 바코드가 발행되었습니다. 삭제 할 수 없습니다.'
      )
    );

  const validatePakBoxId = (grid: any): string | null =>
    validateSelectedField(
      grid,
      'pakBoxId',
      t(
        'com.label.- 해당 품목 List는 박스 포장되었습니다. 삭제 할 수 없습니다.',
        '- 해당 품목 List는 박스 포장되었습니다. 삭제 할 수 없습니다.'
      )
    );

  const validateCtnrId = (grid: any): string | null =>
    validateSelectedField(
      grid,
      'ctnrId',
      t(
        'com.label.- CTNR ID가 존재합니다. 삭제 할 수 없습니다.',
        '- CTNR ID가 존재합니다. 삭제 할 수 없습니다.'
      )
    );

  const btnDelRow = () => {
    const grid = gridRef.current;
    if (!grid) return;

    let msg: string | null = null;
    if (id === 'BARCODE') {
      // 둘 중 하나라도 삭제 불가면 msg 에 에러 메시지 들어감
      msg = validatePakBoxId(grid) || validateRrfidTagUseYn(grid);
    } else {
      msg = validateCtnrId(grid);
    }

    if (msg) {
      openMessageBar({
        type: 'error',
        content: msg,
      });
      return;
    }

    const selectedRowNodes = gridRef.current.selectedRows;
    const selectedIds = selectedRowNodes
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (itemData[item].crudKey === CrudCode.CREATE) {
        delete itemData[item];
      } else {
        itemData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = itemData.filter((element) => element !== undefined);
    setItemData(filteredData || []);
  };

  const btnBCodeTemplateDownload = async () => {
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.TPL_SETUP_ITEM_LIST,
      excpParam.elmPrjId ?? '',
      [],
      excpParam?.excpId ?? ''
    ).finally(() => openLoading(false));
  };

  const btnBCodeTemplateUpload = async (file) => {
    openLoading(true);
    uploadExcelUtMatrixTemplates(
      file,
      'TPL_SETUP_ITEM_REGISTER',
      'NORMAL',
      'TB_EELMB_PRJ_PAK_ITEM_M',
      excpParam.elmPrjId,
      'PRJ_ITEM_REGISTER'
    )
      .then((response) => {
        if (response?.data.x_result == 'OK') {
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
          });
          onSearch();
        } else {
          setExcelValidCondition(response?.data.p_xls_upload_id);
          setExcelValidModalOpen(true);
        }
      })
      .finally(() => {
        openLoading(false);
      });
  };

  const btnTemplateDownload = async () => {
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.TPL_SETUP_ITEM_MNL,
      excpParam.elmPrjId ?? '',
      [],
      excpParam?.excpId ?? ''
    ).finally(() => openLoading(false));
  };

  const btnTemplateUpload = async (file) => {
    openLoading(true);
    uploadExcelUtMatrixTemplates(
      file,
      'TPL_SETUP_ITEM_MNL_REGISTER',
      'NORMAL',
      'TB_EELMB_PRJ_PAK_ITEM_M',
      excpParam.elmPrjId,
      'PRJ_ITEM_MNL_REGISTER'
    )
      .then((response) => {
        if (response?.data.x_result == 'OK') {
          openMessageBar({
            type: 'confirm',
            content: t('setup.label.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
          });
          onSearch();
        } else {
          setExcelValidCondition(response?.data.p_xls_upload_id);
          setExcelValidModalOpen(true);
        }
      })
      .finally(() => {
        openLoading(false);
      });
  };

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      flexRef,
      t('setup.label.품목 List 등록/조회', '품목 List 등록/조회'),
      t('setup.label.품목 List', '품목 List')
    );
  };

  const btnSelectBmkYn = () => {
    const grid = gridRef.current;
    if (!grid) return;

    const rows = grid.rows;
    const len = rows.length;
    if (!rows || len === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.데이터가 없습니다.', '데이터가 없습니다.'),
      });
      return;
    }

    const selectedRows: ItemList[] = [];
    for (const row of rows) {
      if (row.isSelected) {
        selectedRows.push(row.dataItem as ItemList);
      }
    }
    if (selectedRows.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('setup.label.선택된 행이 없습니다.', '선택된 행이 없습니다.'),
      });
      return;
    }

    grid.beginUpdate();
    try {
      for (let i = 0; i < len; i++) {
        const row = rows[i];

        if (!row.isSelected) continue;

        const item = row.dataItem;
        if (!item) continue;

        item.bmkYn = 'Y';
        item.crudKey = CrudCode.UPDATE;
      }
    } finally {
      grid.endUpdate();
    }

    grid.collectionView?.refresh?.();
  };

  const validateSetup = (itemData: ItemList[]): string | null => {
    if (!itemData || itemData.length === 0) {
      return t('setup.label.저장할 데이터가 없습니다.', '저장할 데이터가 없습니다.');
    }

    const msgs: string[] = [];

    itemData.forEach((row, rowIndex) => {
      if (row.crudKey !== CrudCode.UPDATE) return;

      const line = rowIndex + 1;

      if (_.isEmpty(row.pakItemNm)) {
        msgs.push(
          `${line}행 - ` + t('setup.label.품목명을 입력해 주세요.', '품목명을 입력해 주세요.')
        );
      }

      const qty = row.pakItemQty as any;
      const isQtyEmpty = qty === null || qty === undefined || qty === '';
      if (isQtyEmpty) {
        msgs.push(`${line}행 - ` + t('setup.label.수량을 입력해 주세요.', '수량을 입력해 주세요.'));
      } else {
        const qtyStr = String(qty).trim();
        if (!/^\d+$/.test(qtyStr)) {
          msgs.push(
            `${line}행 - ` +
              t('setup.label.수량은 숫자만 입력 가능합니다.', '수량은 숫자만 입력 가능합니다.')
          );
        }
      }

      const unit = (row.qtyUnitCd ?? '').toString().trim();
      if (!unit) {
        msgs.push(`${line}행 - ` + t('setup.label.단위를 선택해 주세요.', '단위를 선택해 주세요.'));
      } else if (unit.length > 10) {
        msgs.push(
          `${line}행 - ` +
            t(
              'setup.label.단위는 10자 이하만 입력 가능합니다.',
              '단위는 10자 이하만 입력 가능합니다.'
            )
        );
      }
    });

    if (!msgs.length) return null;
    return msgs.join('\n');
  };

  const btnSave = () => {
    const msg = validateSetup(itemData);
    if (msg) {
      openMessageBar({ type: 'error', content: msg });
      return false;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        const response = await saveItemList({
          pakItemList: itemData,
        });
        if (response == null || response?.successOrNot === 'N') {
          if (response?.statusCode === 'DUPLICATED_VALUE_ERROR') {
            openMessageBar({
              type: 'error',
              content: t(
                'setup.label.M-CODE, 품목 명이 동일하게 2개 이상 있습니다.',
                'M-CODE, 품목 명이 동일하게 2개 이상 있습니다.'
              ),
            });
          } else {
            openMessageBar({
              type: 'error',
              content: t('com.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.'),
            });
          }
          return;
        }
        openMessageBar({
          type: 'confirm',
          content: t('com.label.저장되었습니다.', '저장되었습니다.'),
        });
        onSearch();
      },
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.품목 List', '품목 List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{itemData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
          {itemCnt && id === 'LIST' && (
            <div className="info warning">
              {t('setup.label.등록률(설비기준)', '등록률(설비기준)')} : {itemCnt?.summary}
            </div>
          )}
        </SubTitleGroup>
        <ControlBtnGroup>
          {isEmailFormat() && (
            <>
              {id === 'LIST' && (
                <Button
                  css={IconButton.button}
                  className="icoNone"
                  onClick={btnSelectBmkYn}
                  sx={{
                    '&&': {
                      color: '#fff',
                      background: ControlFilldBG.Primary,
                      '&:hover': {
                        background: ControlFilldBG.PrimaryHover,
                        color: '#fff',
                      },
                    },
                  }}
                >
                  {t('com.button.발행대상 일괄지정', '발행대상 일괄지정')}
                </Button>
              )}
              {id !== 'LIST' && (
                <>
                  <Button
                    css={IconButton.button}
                    className="Exceldown"
                    onClick={id === 'BARCODE' ? btnBCodeTemplateDownload : btnTemplateDownload}
                    disableRipple
                  >
                    {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
                  </Button>
                  <ExcelUploadButton
                    onCallback={id === 'BARCODE' ? btnBCodeTemplateUpload : btnTemplateUpload}
                  />
                  <Button
                    css={IconButton.button}
                    className="delRow"
                    onClick={btnDelRow}
                    disableRipple
                  >
                    {t('com.button.행삭제', '행삭제')}
                  </Button>
                </>
              )}
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={onSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'itemGrid'}
            layoutDefinition={layoutDefinition}
            changeShownColumns={(result) => {
              gridRef.current?.changeColumn?.(result);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'itemGrid'}
        layoutDefinition={layoutDefinition}
        rowData={itemData}
        isSelector={isEmailFormat()}
        initialized={onInitialized}
        height={462}
      />
      {isOpenCellTextarea && (
        <WJCellTextarea
          grid={flexRef}
          hitTest={hitTest}
          close={() => setOpenCellTextarea(false)}
          textAreaByteMax={4000}
          isReadOnly={id === 'LIST'}
        />
      )}
      {isEmailFormat() && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

export default SetupItemList;
